/*─────────────────────────────────────────────────────────────────────────────
 * AdminRoutes.tsx
 *
 * Route group for the Admin section of the menu:
 *   - AI Integration (providers, profiles, agents)
 *   - Firewall (services, rules, geo, access control, settings)
 *   - Network (settings, OpenVPN)
 *   - Maintenance (backup, cleanup, cron, custom tasks)
 *   - System Settings (branding, misc, email, templates, logging)
 *   - Multi-Tenant (domains, tenants)
 *   - Licensing
 *   - Apps (Switchboard SSO)
 *
 * All routes that require admin-level access and are not already
 * covered by SystemRoutes.tsx are declared here.
 *─────────────────────────────────────────────────────────────────────────────*/

import { Route } from 'react-router-dom';

import {
  AccessControlProtectedRoute,
  AdminCronJobsProtectedRoute,
  AdminCronProfilesProtectedRoute,
  AiAgentsProtectedRoute,
  AiProfilesProtectedRoute,
  AiProvidersProtectedRoute,
  AiToolProfilesProtectedRoute,
  AppearanceProtectedRoute,
  EslUsersProtectedRoute,
  FirewallRulesProtectedRoute,
  FirewallServicesProtectedRoute,
  GeoFirewallProtectedRoute,
  OpenVpnClientProtectedRoute,
  OpenVpnServerProtectedRoute,
  AiTranslatorProfilesProtectedRoute
} from '../components/ProtectedRoute';

// AI Integration
import { AiProfilesPage, AiProfilesFormView } from '../modules/ai-profiles';
import { AiProvidersPage, AiProvidersFormView } from '../modules/ai-providers';
import { AiAgentsPage, AiAgentFormView, AiAgentCallsPage } from '../modules/ai-agents';
import AiChatbotsPage from '../modules/ai-chatbots/AiChatbotsPage';
import AiChatbotFormView from '../modules/ai-chatbots/AiChatbotFormView';
import { AiToolProfilesPage, AiToolProfileFormView } from '../modules/ai-tool-profiles';
import AiTranslatorProfilesPage from '../modules/ai-translator-profiles/AiTranslatorProfilesPage';
import AiTranslatorProfileFormView from '../modules/ai-translator-profiles/AiTranslatorProfileFormView';


// Firewall
import FirewallServicesPage from '../modules/firewallServices';
import FirewallRulesPage from '../modules/firewallRules';
import GeoFirewallPage from '../modules/geoFirewall';
import VoipblPage from '../modules/voipbl/VoipblPage';
import AclPage from '../modules/acl/AclPage';
import AccessControlPage from '../modules/accessControl';
import FirewallSettingsPage from '../modules/firewallSettings';

// Network
import { NetworkSettingsPage, NetworkSettingFormView } from '../modules/networkSettings';

// Maintenance
import { BackupRestorePage } from '../modules/maintenance/backup-restore';
import SystemCleanupPage from '../modules/maintenance/system/pages/SystemCleanupPage';
import SystemCleanupFormPage from '../modules/maintenance/system/pages/SystemCleanupFormPage';
import CronProfilesPage from '../modules/cronProfiles/CronProfilesPage';
import CronProfileForm from '../modules/cronProfiles/CronProfileForm';
import { CustomTasksPage, CustomTaskFormPage } from '../modules/customTasks';

// System Settings
import { BrandingPage, BrandingFormView } from '../modules/branding';
import EmailTemplatesListView from '../modules/email-templates/EmailTemplatesListView';
import EmailTemplatesFormView from '../modules/email-templates/EmailTemplatesFormView';
import { CertificatesPage, CertificateForm } from '../modules/certificates';
import HttpServerPage from '../modules/httpServer/HttpServerPage';
import OpenVPNPage from '../modules/openvpn/OpenVPNPage';
import OpenVPNClientPage from '../modules/openvpn-client/OpenVPNClientPage';
import WireGuardClientPage from '../modules/wireguard-client/WireGuardClientPage';
import EslUsersPage from '../modules/eslUsers/EslUsersPage';
import EslUserFormView from '../modules/eslUsers/EslUserFormView';

// Licensing
import { LicensingPage } from '../modules/licensing';

// External apps SSO
import SwitchboardSSOPage from '../pages/apps/SwitchboardSSOPage';
import PortalSSOPage from '../pages/apps/PortalSSOPage';

// Protected route wrappers
import {
  SystemMiscProtectedRoute,
  EmailTemplatesProtectedRoute,
  BackupRestoreProtectedRoute,
  LicensingProtectedRoute,
} from '../components/ProtectedRoute';

/**
 * AdminRoutes
 *
 * Returns JSX fragment of <Route> elements for the entire admin section.
 * Consumed by App.tsx inside the authenticated <Routes> block.
 *
 * Design note: Routes without a dedicated ProtectedRoute HOC are treated as
 * admin-only by convention (only reachable via admin menu items whose
 * permission is enforced server-side).
 */
export const AdminRoutes = () => (
  <>
    {/* ── AI Integration ──────────────────────────────────────────────────── */}
    <Route path="/admin/ai-integration/providers" element={<AiProvidersProtectedRoute><AiProvidersPage /></AiProvidersProtectedRoute>} />
    <Route path="/admin/ai-integration/providers/new" element={<AiProvidersProtectedRoute><AiProvidersFormView /></AiProvidersProtectedRoute>} />
    <Route path="/admin/ai-integration/providers/:id" element={<AiProvidersProtectedRoute><AiProvidersFormView /></AiProvidersProtectedRoute>} />
    <Route path="/admin/ai-integration/profiles" element={<AiProfilesProtectedRoute><AiProfilesPage /></AiProfilesProtectedRoute>} />
    <Route path="/admin/ai-integration/profiles/new" element={<AiProfilesProtectedRoute><AiProfilesFormView /></AiProfilesProtectedRoute>} />
    <Route path="/admin/ai-integration/profiles/:id" element={<AiProfilesProtectedRoute><AiProfilesFormView /></AiProfilesProtectedRoute>} />
    <Route path="/admin/ai-integration/agents" element={<AiAgentsProtectedRoute><AiAgentsPage /></AiAgentsProtectedRoute>} />
    <Route path="/admin/ai-integration/agents/new" element={<AiAgentsProtectedRoute><AiAgentFormView /></AiAgentsProtectedRoute>} />
    <Route path="/admin/ai-integration/agents/:id" element={<AiAgentsProtectedRoute><AiAgentFormView /></AiAgentsProtectedRoute>} />
    {/* Agent call history — read-only report view */}
    <Route path="/admin/ai-integration/agents/:id/calls" element={<AiAgentsProtectedRoute><AiAgentCallsPage /></AiAgentsProtectedRoute>} />
    
    <Route path="/admin/ai-integration/chatbots" element={<AiAgentsProtectedRoute><AiChatbotsPage /></AiAgentsProtectedRoute>} />
    <Route path="/admin/ai-integration/chatbots/new" element={<AiAgentsProtectedRoute><AiChatbotFormView /></AiAgentsProtectedRoute>} />
    <Route path="/admin/ai-integration/chatbots/:id" element={<AiAgentsProtectedRoute><AiChatbotFormView /></AiAgentsProtectedRoute>} />
    <Route path="/admin/ai-integration/chatbots/:id/calls" element={<AiAgentsProtectedRoute><AiAgentCallsPage /></AiAgentsProtectedRoute>} />
    {/* Tool Profiles */}
    <Route path="/admin/ai-integration/tool-profiles" element={<AiToolProfilesProtectedRoute><AiToolProfilesPage /></AiToolProfilesProtectedRoute>} />
    <Route path="/admin/ai-integration/tool-profiles/new" element={<AiToolProfilesProtectedRoute><AiToolProfileFormView /></AiToolProfilesProtectedRoute>} />
    <Route path="/admin/ai-integration/tool-profiles/:id" element={<AiToolProfilesProtectedRoute><AiToolProfileFormView /></AiToolProfilesProtectedRoute>} />

    {/* Translator Profiles */}
    <Route path="/admin/ai-integration/translator-profiles" element={<AiTranslatorProfilesProtectedRoute><AiTranslatorProfilesPage /></AiTranslatorProfilesProtectedRoute>} />
    <Route path="/admin/ai-integration/translator-profiles/new" element={<AiTranslatorProfilesProtectedRoute><AiTranslatorProfileFormView /></AiTranslatorProfilesProtectedRoute>} />
    <Route path="/admin/ai-integration/translator-profiles/:id" element={<AiTranslatorProfilesProtectedRoute><AiTranslatorProfileFormView /></AiTranslatorProfilesProtectedRoute>} />


    {/* ── Firewall ────────────────────────────────────────────────────────── */}
    <Route path="/admin/firewall/services" element={<FirewallServicesProtectedRoute><FirewallServicesPage /></FirewallServicesProtectedRoute>} />
    <Route path="/admin/firewall/rules" element={<FirewallRulesProtectedRoute><FirewallRulesPage /></FirewallRulesProtectedRoute>} />
    <Route path="/admin/firewall/geo" element={<GeoFirewallProtectedRoute><GeoFirewallPage /></GeoFirewallProtectedRoute>} />
    <Route path="/admin/firewall/voipbl" element={<GeoFirewallProtectedRoute><VoipblPage /></GeoFirewallProtectedRoute>} />
    <Route path="/admin/firewall/acl" element={<GeoFirewallProtectedRoute><AclPage /></GeoFirewallProtectedRoute>} />
    <Route path="/admin/firewall/access-control" element={<AccessControlProtectedRoute><AccessControlPage /></AccessControlProtectedRoute>} />
    <Route path="/admin/firewall/settings" element={<AccessControlProtectedRoute><FirewallSettingsPage /></AccessControlProtectedRoute>} />

    {/* ── Network ─────────────────────────────────────────────────────────── */}
    <Route path="/admin/network/settings" element={<OpenVpnServerProtectedRoute><NetworkSettingsPage /></OpenVpnServerProtectedRoute>} />
    <Route path="/admin/network/settings/:id" element={<OpenVpnServerProtectedRoute><NetworkSettingFormView /></OpenVpnServerProtectedRoute>} />
    <Route path="/admin/network/openvpn"        element={<OpenVpnServerProtectedRoute><OpenVPNPage /></OpenVpnServerProtectedRoute>} />
    <Route path="/admin/network/openvpn-client" element={<OpenVpnClientProtectedRoute><OpenVPNClientPage /></OpenVpnClientProtectedRoute>} />
    <Route path="/admin/network/wireguard"      element={<OpenVpnClientProtectedRoute><WireGuardClientPage /></OpenVpnClientProtectedRoute>} />

    {/* ── Maintenance ─────────────────────────────────────────────────────── */}
    <Route path="/admin/maintenance/backup" element={
      <BackupRestoreProtectedRoute>
        <BackupRestorePage />
      </BackupRestoreProtectedRoute>
    } />
    <Route path="/admin/maintenance/system" element={
      <BackupRestoreProtectedRoute>
        <SystemCleanupPage />
      </BackupRestoreProtectedRoute>
    } />
    <Route path="/admin/maintenance/system/:tenantId" element={
      <BackupRestoreProtectedRoute>
        <SystemCleanupFormPage />
      </BackupRestoreProtectedRoute>
    } />
    <Route path="/admin/maintenance/cron-profiles" element={<AdminCronProfilesProtectedRoute><CronProfilesPage /></AdminCronProfilesProtectedRoute>} />
    <Route path="/admin/maintenance/cron-profiles/new" element={<AdminCronProfilesProtectedRoute><CronProfileForm /></AdminCronProfilesProtectedRoute>} />
    <Route path="/admin/maintenance/cron-profiles/:id" element={<AdminCronProfilesProtectedRoute><CronProfileForm /></AdminCronProfilesProtectedRoute>} />
    <Route path="/admin/maintenance/tasks" element={<AdminCronJobsProtectedRoute><CustomTasksPage /></AdminCronJobsProtectedRoute>} />
    <Route path="/admin/maintenance/tasks/:id" element={<AdminCronJobsProtectedRoute><CustomTaskFormPage /></AdminCronJobsProtectedRoute>} />

    {/* ── System Settings (admin-specific) ────────────────────────────────── */}
    <Route path="/admin/system-settings/branding" element={
      <SystemMiscProtectedRoute>
        <BrandingPage />
      </SystemMiscProtectedRoute>
    } />
    <Route path="/admin/system-settings/branding/:id" element={
      <SystemMiscProtectedRoute>
        <BrandingFormView />
      </SystemMiscProtectedRoute>
    } />
    <Route path="/admin/system-settings/email-template" element={
      <EmailTemplatesProtectedRoute>
        <EmailTemplatesListView />
      </EmailTemplatesProtectedRoute>
    } />
    <Route path="/admin/system-settings/email-template/new" element={
      <EmailTemplatesProtectedRoute>
        <EmailTemplatesFormView />
      </EmailTemplatesProtectedRoute>
    } />
    <Route path="/admin/system-settings/email-template/:id" element={
      <EmailTemplatesProtectedRoute>
        <EmailTemplatesFormView />
      </EmailTemplatesProtectedRoute>
    } />
    <Route path="/admin/system-settings/certificates" element={
      <BackupRestoreProtectedRoute>
        <CertificatesPage />
      </BackupRestoreProtectedRoute>
    } />
    <Route path="/admin/system-settings/certificates/:uuid" element={
      <BackupRestoreProtectedRoute>
        <CertificateForm />
      </BackupRestoreProtectedRoute>
    } />
    <Route path="/admin/system-settings/http-server" element={
      <BackupRestoreProtectedRoute>
        <HttpServerPage />
      </BackupRestoreProtectedRoute>
    } />

    {/* ── ESL Users (PBX settings / admin) ────────────────────────────────── */}
    <Route path="/settings/pbx/esl-users" element={<EslUsersProtectedRoute><EslUsersPage /></EslUsersProtectedRoute>} />
    <Route path="/settings/pbx/esl-users/new" element={<EslUserFormView />} />
    <Route path="/settings/pbx/esl-users/:id" element={<EslUserFormView />} />

    {/* ── Licensing ───────────────────────────────────────────────────────── */}
    <Route path="/admin/licensing" element={
      <LicensingProtectedRoute>
        <LicensingPage />
      </LicensingProtectedRoute>
    } />

    {/* ── External Apps ───────────────────────────────────────────────────── */}
    {/* Switchboard SSO redirect — opens the Switchboard app with a JWT token */}
    <Route path="/apps/switchboard" element={<AppearanceProtectedRoute><SwitchboardSSOPage /></AppearanceProtectedRoute>} />
    <Route path="/apps/portal" element={<AppearanceProtectedRoute><PortalSSOPage /></AppearanceProtectedRoute>} />
    {/* Billing SSO redirect — opens the Billing app with a JWT token */}
  </>
);
